import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import styles from "./FlashPromotion.module.css";
import CountdownRing from "./CountdownRing";
import FlashPromotionContext from "~/contexts/FlashPromotionContext";
import OrderContext from "~/contexts/OrderContext";
import TaxContext from "~/contexts/TaxContext";
import ThemeContext from "~/contexts/ThemeContext";
import {
  clampDurationSeconds,
  decideFlashPromotion,
  formatSuccessMessage,
} from "~/entities/flashPromotionEntities";
import { useFlashPromotionTimer } from "~/hooks/useFlashPromotionTimer";
import {
  findMenuItemByProductCode,
  resolveOutstandingRequirement,
} from "~/helpers/flashRewardHelpers";
import { generateLine } from "~/helpers/cartHelpers";
import MenuContext from "~/contexts/MenuContext";
import { storage, STORAGE_KEYS } from "~/storage";
import { uploadcareConfig } from "~/configs/uploadcareConfig";

type CardState = "offer" | "redeeming" | "success" | "error";

const readSeen = (): string[] => {
  const raw = storage.get(STORAGE_KEYS.FLASH_PROMOS_DISMISSED);
  if (!raw) {
    return [];
  }
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const markSeen = (id: string) => {
  const seen = readSeen();
  if (!seen.includes(id)) {
    storage.set(
      STORAGE_KEYS.FLASH_PROMOS_DISMISSED,
      JSON.stringify([...seen, id])
    );
  }
};

const readExpiries = (): Record<string, number> => {
  const raw = storage.get(STORAGE_KEYS.FLASH_PROMOS_EXPIRED);
  if (!raw) {
    return {};
  }
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const markExpired = (id: string) => {
  const expiries = readExpiries();
  storage.set(
    STORAGE_KEYS.FLASH_PROMOS_EXPIRED,
    JSON.stringify({ ...expiries, [id]: (expiries[id] ?? 0) + 1 })
  );
};

/**
 * The flash promotion card: a timed countdown offer above the menu.
 *
 * Tapping redeems the campaign's shared voucher through the ordinary promo-code
 * path, so a code redeemed by tapping and a code typed by hand behave
 * identically from there on, and all discount logic stays server side.
 *
 * Client-only: it depends on timers and sessionStorage, so it renders nothing
 * during SSR rather than risking a hydration mismatch.
 */
const FlashPromotionCard: FC = () => {
  const { flashPromotion } = useContext(FlashPromotionContext);
  const { orderSummary, readonlyCart, redeemPromotionCode, addOrderLine } =
    useContext(OrderContext);
  const { menu } = useContext(MenuContext);
  const { formatAmount } = useContext(TaxContext);
  const { template } = useContext(ThemeContext);

  const [mounted, setMounted] = useState(false);
  const [state, setState] = useState<CardState>("offer");
  const [dismissed, setDismissed] = useState(false);
  // Drives the enter transition. Set on the frame after the card is in the DOM
  // so the browser has a "from" state to transition out of.
  const [entered, setEntered] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [seen, setSeen] = useState<string[]>([]);
  const [expiries, setExpiries] = useState<Record<string, number>>({});
  const [tabHidden, setTabHidden] = useState(false);
  // The reward is added once, on the transition into success
  const rewardAdded = useRef(false);

  useEffect(() => {
    // Dev escape hatch. Suppression is per-session and deliberately sticky, which
    // makes the card hard to look at twice while working on it — and asking
    // someone to type a sessionStorage command every time is not a workflow.
    // ?flashReset clears both keys and gives you the card back.
    if (import.meta.env.DEV && typeof window !== "undefined") {
      if (new URLSearchParams(window.location.search).has("flashReset")) {
        storage.remove(STORAGE_KEYS.FLASH_PROMOS_DISMISSED);
        storage.remove(STORAGE_KEYS.FLASH_PROMOS_EXPIRED);
        console.info(
          "[flash promotion] ?flashReset — cleared session suppression. " +
            "If the card still does not show, the order itself still carries " +
            "the promo code; remove it from the cart or start a new order."
        );
      }
    }

    setMounted(true);
    setSeen(readSeen());
    setExpiries(readExpiries());
  }, []);

  useEffect(() => {
    const onVisibility = () => setTabHidden(document.hidden);
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  const settled = state === "success" || state === "error";

  const duration = clampDurationSeconds(
    flashPromotion?.displayDurationSeconds ?? 0
  );

  // One decision, one reason. The timer needs eligibility before phase exists,
  // so it asks with phase "waiting" and the real answer is recomputed below —
  // the same function either way, so the two cannot disagree.
  const decisionInput = {
    campaign: flashPromotion,
    mounted,
    dismissed,
    readonlyCart,
    promoCode: orderSummary.promoCode,
    redemptionKey: orderSummary.redemptionKey,
    cartLineCount: orderSummary.lines?.length ?? 0,
    dismissedIds: seen,
    expiryCounts: expiries,
  };

  const { eligible } = decideFlashPromotion({
    ...decisionInput,
    phase: "waiting" as const,
  });

  const { phase, secondsRemaining } = useFlashPromotionTimer({
    // Once the code attaches, eligibility drops (promoCode is now set) — hold
    // the card open on its outcome rather than yanking it away mid-sentence
    eligible: eligible || state === "redeeming" || settled,
    delaySeconds: flashPromotion?.delaySeconds ?? 0,
    displayDurationSeconds: duration,
    // Freeze while redeeming or settled: the card must not expire mid-request,
    // and the success state gets its own dismissal timer below
    paused: tabHidden || state !== "offer",
  });

  const EXIT_MS = 150;

  const dismiss = useCallback(() => {
    if (flashPromotion) {
      markSeen(flashPromotion.id);
    }
    // Let it animate out rather than vanishing — an element disappearing
    // without transition reads as a glitch
    setExiting(true);
    setTimeout(() => setDismissed(true), EXIT_MS);
  }, [flashPromotion]);

  // Count the miss rather than banking it as a decision — see MAX_EXPIRIES
  useEffect(() => {
    if (phase === "expired" && flashPromotion) {
      markExpired(flashPromotion.id);
    }
  }, [phase, flashPromotion]);

  // The submit settled. Either the order now carries our code, or it does not
  // and the redemption failed — an expired voucher, or a code that will not
  // attach here. A card that vanished on failure would look identical to one
  // that worked, so the error stays on screen with the tap still available.
  useEffect(() => {
    if (state !== "redeeming" || !flashPromotion) {
      return;
    }

    // Success is "the code is on the order", whenever that becomes true.
    //
    // Deliberately NOT keyed off orderLoading going false: this effect runs on
    // the same render that sets `redeeming`, before the fetcher has started
    // submitting, so a not-loading-yet state is indistinguishable from a
    // finished one and every redemption was instantly judged a failure.
    if (orderSummary.promoCode === flashPromotion.voucherUnlockCode) {
      // Deliberately NOT marked as seen.
      //
      // While the code is on the order the card is suppressed anyway — the
      // decision function treats an attached promo code as a reason not to
      // offer — so a session flag would add nothing here. All it would change
      // is what happens after the customer REMOVES the voucher, and killing the
      // offer then is incoherent: the charge guard's own advice is "Remove it"
      // when they are under the minimum, so taking that advice would cost them
      // the offer permanently. Someone with no code on their order has not
      // taken anything, and can be offered it again.
      //
      // Explicit dismissal still sticks. That is a decision; this is not.
      setState("success");
      return;
    }

    // Failure is the absence of that after a reasonable wait. Slower than any
    // realistic round trip, because calling it early is what caused the bug
    // above.
    const timer = setTimeout(() => setState("error"), 12000);
    return () => clearTimeout(timer);
  }, [state, orderSummary.promoCode, flashPromotion]);

  /**
   * Put the reward in the cart the moment the code attaches.
   *
   * MOBI adds the free item immediately and makes the customer remove it if
   * they do not want it, and the reasoning holds: once the garlic knot is in
   * your cart, failing to reach the minimum feels like losing something you
   * have rather than missing something you were offered. Endowment beats a
   * promise, and it is most of what makes this feature worth building.
   *
   * All or nothing. If any product in the offer is missing from the menu being
   * browsed, add none of them — two of three pinwheels and no discount is the
   * worst outcome available, and the customer blames the card for it.
   *
   * A genuine choice ("any two burgers") is left alone here; that needs the
   * picker rather than a guess about which burger they wanted.
   */
  useEffect(() => {
    if (
      state !== "success" ||
      rewardAdded.current ||
      !flashPromotion ||
      !menu
    ) {
      return;
    }
    // unlockedPromotions arrives with the order response, which can be a render
    // or two behind the code appearing. Claiming the once-guard before it lands
    // burns the single attempt on an empty list — so it is claimed at the point
    // of actually adding, below.
    const promotion = orderSummary.unlockedPromotions?.find(
      (p) => p.code === flashPromotion.promotionCode
    );
    if (!promotion) {
      return;
    }

    const steps = resolveOutstandingRequirement(promotion, orderSummary);
    if (!steps.length || steps.some((s) => s.productCodes.length !== 1)) {
      return;
    }

    const resolved = steps.flatMap((step) => {
      const item = findMenuItemByProductCode(menu, step.productCodes[0]);
      return item ? [{ item, qty: step.qty }] : [];
    });
    if (resolved.length !== steps.length) {
      return;
    }

    rewardAdded.current = true;

    resolved.forEach(({ item, qty }) => {
      const line = generateLine(item, menu.code);
      line.item.qty = qty;
      addOrderLine(line);
    });
  }, [state, flashPromotion, menu, orderSummary, addOrderLine]);

  // Let the success state be read, then get out of the way
  useEffect(() => {
    if (state !== "success") {
      return;
    }
    const timer = setTimeout(() => {
      setExiting(true);
      setTimeout(() => setDismissed(true), EXIT_MS);
    }, 4000);
    return () => clearTimeout(timer);
  }, [state]);

  const decision = decideFlashPromotion({ ...decisionInput, phase });

  // The decision governs whether to OFFER the card. Once the customer has
  // tapped, it stays on screen through its outcome regardless: attaching the
  // code sets orderSummary.promoCode, which the decision correctly treats as a
  // reason not to offer — but yanking the card away mid-sentence would read as
  // a failure rather than a success.
  const inFlightOrSettled = state !== "offer";
  const visible =
    decision.show ||
    (inFlightOrSettled && mounted && !!flashPromotion && !dismissed);
  const suppressionReason = decision.reason;

  useEffect(() => {
    if (!visible) {
      return;
    }
    // Next frame, so the initial (pre-transition) styles are committed first
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, [visible]);

  useEffect(() => {
    if (!visible) {
      return;
    }
    // Dismissal is explicit — Escape or the close control. Tapping the menu
    // behind should not count; the customer is trying to order, not answer us.
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        dismiss();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [visible, dismiss]);

  const minimumSpendLabel = useMemo(
    () => formatAmount(flashPromotion?.minimumSpend ?? 0) ?? "",
    [formatAmount, flashPromotion?.minimumSpend]
  );

  useEffect(() => {
    if (!import.meta.env.DEV || visible) {
      return;
    }
    console.info(`[flash promotion] not shown: ${suppressionReason}`);
  }, [visible, suppressionReason]);

  if (!visible || !flashPromotion) {
    // Also expose it on the DOM, so a Playwright run can assert on the reason
    // rather than on the absence of something
    return import.meta.env.DEV ? (
      <span hidden data-flash-suppressed={suppressionReason ?? "unknown"} />
    ) : null;
  }

  const onRedeem = () => {
    if (state === "redeeming" || state === "success") {
      return;
    }
    setState("redeeming");
    // Deliberately NOT marked engaged here. Whether the code attaches is not
    // known until the order comes back, and a redemption that failed is not a
    // decision the customer made — banking it would deny them an offer they
    // never actually received. Marked on success instead.
    redeemPromotionCode(flashPromotion.voucherUnlockCode);
  };

  const accent = template.global.button.primaryButtonColour;

  return (
    <div className={styles.anchor}>
      <div
        className={styles.wrapper}
        style={{ borderColor: accent }}
        data-state={exiting ? "exiting" : entered ? "entered" : "entering"}
      >
        <div className={styles.card}>
          {state === "success" ? (
            <div className={styles.content}>
              <span className={styles.header}>
                {flashPromotion.successHeader}
              </span>
              <span className={styles.message}>
                {formatSuccessMessage(
                  flashPromotion.successMessage,
                  minimumSpendLabel
                )}
              </span>
            </div>
          ) : (
            <button
              type="button"
              className={styles.offerButton}
              onClick={onRedeem}
              disabled={state === "redeeming"}
              // Explicit name. The computed one would be the ring's "10 seconds
              // remaining" concatenated with the header and message — noisy read
              // aloud, and ambiguous enough that a test selector hit the dismiss
              // control instead of this one.
              aria-label={`${flashPromotion.header}. ${flashPromotion.message}`}
            >
              <CountdownRing
                secondsRemaining={secondsRemaining}
                totalSeconds={duration}
                colour={accent}
              />
              <span className={styles.content}>
                <span className={styles.header}>{flashPromotion.header}</span>
                <span className={styles.message}>
                  {state === "error"
                    ? "That offer could not be added. Tap to try again."
                    : flashPromotion.message}
                </span>
              </span>
              {flashPromotion.image && (
                <img
                  className={styles.image}
                  src={uploadcareConfig.getUrl(
                    flashPromotion.image.fileId,
                    flashPromotion.image.modifiers
                  )}
                  alt=""
                />
              )}
            </button>
          )}
          <button
            type="button"
            className={styles.dismiss}
            onClick={dismiss}
            aria-label="Dismiss offer"
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
};

export default FlashPromotionCard;
